import { Sha256 } from '@aws-crypto/sha256-js'
import { fetchAuthSession } from 'aws-amplify/auth'

// Local SigV4 presigning for S3 GETs, plus a self-check of the result.
// Ported from the mobile app (JJA-app src/helpers/presign.ts), where it
// replaced Amplify's getUrl: getUrl signs an off-spec shape that S3
// intermittently rejects with SignatureDoesNotMatch; presignGetObjectUrl
// below signs the spec/CLI shape, and checkPresignedUrl re-derives the
// signature from a URL's own query params so a bad URL is caught before S3
// ever sees it.

const hash = (
  key: Uint8Array | string | undefined,
  data: string
): Uint8Array => {
  const sha = new Sha256(key)
  sha.update(data)
  return sha.digestSync()
}

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

export const escapeUri = (uri: string): string =>
  encodeURIComponent(uri).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  )

// Deriving the signing key is 4 HMACs; it only changes per key/date/region, so
// memoize it. With 30 image tiles this is ~10 crypto ops per screen instead of
// ~300 on the JS thread.
let signingKeyCache: { id: string, key: Uint8Array } | undefined
const getSigningKey = (
  secretAccessKey: string,
  accessKeyId: string,
  shortDate: string,
  region: string,
  service: string
): Uint8Array => {
  const id = `${accessKeyId}/${shortDate}/${region}/${service}`
  if (signingKeyCache?.id === id) {
    return signingKeyCache.key
  }
  let key = hash(`AWS4${secretAccessKey}`, shortDate)
  key = hash(key, region)
  key = hash(key, service)
  key = hash(key, 'aws4_request')
  signingKeyCache = { id, key }
  return key
}

export interface PresignCheck {
  // true when the URL's signature, key id and token all agree with the
  // current session credentials (fail-open on verifier errors)
  ok: boolean
  summary: string
}

export interface SigningCredentials {
  accessKeyId: string
  secretAccessKey: string
  sessionToken?: string
}

export const checkPresignedUrl = async (
  href: string,
  // pass the already-fetched session credentials to avoid an extra
  // session read per image
  knownCreds?: SigningCredentials
): Promise<PresignCheck> => {
  try {
    const url = new URL(href)
    const provided = url.searchParams.get('X-Amz-Signature') ?? 'none'
    const amzDate = url.searchParams.get('X-Amz-Date') ?? ''
    const credParam = url.searchParams.get('X-Amz-Credential') ?? ''
    const urlToken = url.searchParams.get('X-Amz-Security-Token') ?? ''
    const [urlKeyId, , region, service] = credParam.split('/')
    const shortDate = amzDate.slice(0, 8)

    // canonical request exactly as SigV4 defines it for a presigned GET
    const params: Array<[string, string]> = []
    url.searchParams.forEach((value, key) => {
      if (key !== 'X-Amz-Signature') {
        params.push([key, value])
      }
    })
    params.sort(([keyA, valA], [keyB, valB]) => {
      if (keyA === keyB) {
        return valA < valB ? -1 : 1
      }
      return keyA < keyB ? -1 : 1
    })
    const canonicalQuery = params
      .map(([key, value]) => `${escapeUri(key)}=${escapeUri(value)}`)
      .join('&')
    // Payload line: Amplify-shaped URLs carry x-amz-content-sha256 in the
    // query and sign with its value; spec/CLI-shaped URLs sign with the
    // literal UNSIGNED-PAYLOAD.
    const payloadLine =
      url.searchParams.get('x-amz-content-sha256') ?? 'UNSIGNED-PAYLOAD'
    const canonical = [
      'GET',
      url.pathname,
      canonicalQuery,
      `host:${url.host}\n`,
      'host',
      payloadLine
    ].join('\n')
    const scope = `${shortDate}/${region}/${service}/aws4_request`
    const stringToSign = [
      'AWS4-HMAC-SHA256',
      amzDate,
      scope,
      toHex(hash(undefined, canonical))
    ].join('\n')

    const creds = knownCreds ?? (await fetchAuthSession()).credentials
    if (creds == null) {
      return { ok: true, summary: 'verify: no session credentials available' }
    }
    const key = getSigningKey(
      creds.secretAccessKey,
      creds.accessKeyId,
      shortDate,
      region,
      service
    )
    const expected = toHex(hash(key, stringToSign))

    const sigMatch = expected === provided
    const keyIdMatch = urlKeyId === creds.accessKeyId
    const tokenMatch = urlToken === (creds.sessionToken ?? '')
    return {
      ok: sigMatch && keyIdMatch && tokenMatch,
      summary: `verify: sig ${
        sigMatch ? 'MATCHES' : 'DIFFERS'
      } w/ current creds, keyId ${keyIdMatch ? 'same' : 'DIFFERENT'}, token ${
        tokenMatch ? 'same' : 'DIFFERENT'
      }`
    }
  } catch (error: any) {
    return { ok: true, summary: `verify failed: ${String(error?.message)}` }
  }
}

// Presigns a GET in the spec/CLI shape: payload line UNSIGNED-PAYLOAD and no
// x-amz-content-sha256 query param. Amplify's getUrl signs the payload line
// as the empty-body hash and relies on S3 honoring its x-amz-content-sha256
// query param; S3 validates that shape most of the time but intermittently
// canonicalizes spec-strictly with UNSIGNED-PAYLOAD and rejects the batch
// with SignatureDoesNotMatch (the random 403s on order images). This shape
// validates under both behaviors — verified against the stage bucket.
// Browsers open at most 6 concurrent HTTP/1.1 connections per host, and S3
// does not speak HTTP/2. S3 answers the same bucket on several equivalent
// hostnames, so spreading keys across them multiplies the number of images
// that can download at once. The choice is a pure function of the key so a
// given object always gets the same URL (and stays cacheable).
const S3_HOSTS: Array<(bucket: string, region: string) => string> = [
  (bucket, region) => `${bucket}.s3.${region}.amazonaws.com`,
  (bucket, region) => `${bucket}.s3.dualstack.${region}.amazonaws.com`,
  (bucket, region) => `${bucket}.s3-${region}.amazonaws.com`
]

export const allS3Hosts = (bucket: string, region: string): string[] =>
  S3_HOSTS.map((host) => host(bucket, region))

export const s3HostForKey = (
  bucket: string,
  region: string,
  key: string,
  // Shifts to a different hostname; a retry uses this so a key is not stuck on
  // one host if that host is the thing failing.
  offset = 0
): string => {
  let acc = 0
  for (let i = 0; i < key.length; i++) {
    acc = (acc * 31 + key.charCodeAt(i)) % 1000003
  }
  return S3_HOSTS[(acc + offset) % S3_HOSTS.length](bucket, region)
}

export const presignGetObjectUrl = (input: {
  bucket: string
  region: string
  key: string // e.g. 'public/<filename>', not URL-encoded
  credentials: SigningCredentials
  expiresInSeconds: number
  hostOffset?: number
  // Fixed signing instant. Callers floor this to a time window so the same
  // object yields a byte-identical URL, which lets the image cache hit.
  signedAt?: Date
}): { url: URL, expiresAt: Date } => {
  const { bucket, region, key, credentials, expiresInSeconds } = input
  const host = s3HostForKey(bucket, region, key, input.hostOffset ?? 0)
  const path = `/${key.split('/').map(escapeUri).join('/')}`

  const now = input.signedAt ?? new Date()
  const amzDate = `${now.toISOString().slice(0, 19).replace(/[-:]/g, '')}Z`
  const shortDate = amzDate.slice(0, 8)
  const scope = `${shortDate}/${region}/s3/aws4_request`

  const params: Array<[string, string]> = [
    ['X-Amz-Algorithm', 'AWS4-HMAC-SHA256'],
    ['X-Amz-Credential', `${credentials.accessKeyId}/${scope}`],
    ['X-Amz-Date', amzDate],
    ['X-Amz-Expires', expiresInSeconds.toString()],
    ['X-Amz-SignedHeaders', 'host'],
    ...(credentials.sessionToken != null
      ? ([['X-Amz-Security-Token', credentials.sessionToken]] as Array<[string, string]>)
      : [])
  ]
  params.sort(([keyA], [keyB]) => (keyA < keyB ? -1 : 1))
  // The wire query is built with the same escaping as the canonical query,
  // so what is signed is byte-identical to what is sent.
  const query = params
    .map(([name, value]) => `${escapeUri(name)}=${escapeUri(value)}`)
    .join('&')

  const canonical = [
    'GET',
    path,
    query,
    `host:${host}\n`,
    'host',
    'UNSIGNED-PAYLOAD'
  ].join('\n')
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    scope,
    toHex(hash(undefined, canonical))
  ].join('\n')
  const signingKey = getSigningKey(
    credentials.secretAccessKey,
    credentials.accessKeyId,
    shortDate,
    region,
    's3'
  )
  const signature = toHex(hash(signingKey, stringToSign))

  return {
    url: new URL(
      `https://${host}${path}?${query}&X-Amz-Signature=${signature}`
    ),
    expiresAt: new Date(now.getTime() + expiresInSeconds * 1000)
  }
}
